import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, BookOpen, Info, X } from 'lucide-react';
import {
    istHandlungsbedarf,
    type NotizAktion,
    type NotizVorschlag,
} from './unterlagenHandbuch';
import { datumDeutsch } from './unterlagenLogic';

interface Props {
    vorschlaege: NotizVorschlag[];
    /** > 0: eine Uebernahme wurde abgelehnt, weil sich so viele Eintraege
     *  inzwischen in der App geaendert haben — die Vorschau ist neu gerechnet. */
    erneut?: number;
    /** Stand des Handbuchs (Export-Zeitpunkt). */
    standIso: string;
    unbekannteFelder: number;
    /** Gesetzt = Uebernehmen gesperrt (Lesemodus, abgeschlossen). */
    gesperrt: string | null;
    onUebernehmen: (auswahl: Record<string, NotizAktion>) => void;
    onAbbrechen: () => void;
}

const AKTION_LABEL: Record<NotizAktion, string> = {
    ersetzen: 'Ersetzen',
    anhaengen: 'Anhängen',
    ueberspringen: 'Überspringen',
};

const Textkasten: React.FC<{ titel: string; text: string; betont?: boolean }> = ({ titel, text, betont }) => (
    <div className="min-w-0">
        <div className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-0.5">{titel}</div>
        <div className={`text-xs whitespace-pre-wrap break-words rounded-lg border px-2 py-1.5 max-h-28 overflow-y-auto ${
            betont ? 'bg-blue-50/60 border-blue-200 text-slate-800' : 'bg-slate-50 border-slate-200 text-slate-600'
        }`}>
            {text || <span className="italic text-slate-400">leer</span>}
        </div>
    </div>
);

/**
 * Vorschau beim Einlesen der Handbuch-Notizen: was sich aendert, wo beide
 * Seiten geaendert wurden, was uebersprungen wird. Nichts wird geschrieben,
 * bevor der Nutzer „Uebernehmen" drueckt — dann in EINEM Schreibvorgang.
 *
 * Per Portal an `document.body`: der Modulinhalt scrollt in einem eigenen
 * Container, und ein `fixed`-Dialog darin haengt am naechsten Vorfahren mit
 * `transform` — eine Falle, sobald jemand einen einbaut (siehe PlanungModul).
 */
export const HandbuchImportDialog: React.FC<Props> = ({
    vorschlaege, erneut = 0, standIso, unbekannteFelder, gesperrt, onUebernehmen, onAbbrechen,
}) => {
    const [auswahl, setAuswahl] = useState<Record<string, NotizAktion>>(() => {
        const start: Record<string, NotizAktion> = {};
        vorschlaege.forEach(v => { if (istHandlungsbedarf(v)) start[v.id] = v.vorschlag; });
        return start;
    });

    const konflikte = useMemo(() => vorschlaege.filter(v => v.art === 'konflikt'), [vorschlaege]);
    const aenderungen = useMemo(() => vorschlaege.filter(v => v.art === 'ersetzen'), [vorschlaege]);
    const uebersprungen = useMemo(
        () => vorschlaege.filter(v => v.art === 'nicht_zuordenbar' || v.art === 'begruendung_bleibt'),
        [vorschlaege],
    );
    const unveraendert = vorschlaege.filter(v => v.art === 'unveraendert').length;
    const anzahl = [...konflikte, ...aenderungen].filter(v => (auswahl[v.id] || v.vorschlag) !== 'ueberspringen').length;

    const setze = (id: string, a: NotizAktion) => setAuswahl(alt => ({ ...alt, [id]: a }));

    return createPortal(
        <div className="fixed inset-0 z-[80] bg-slate-900/50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col min-h-0">
                <div className="flex items-start gap-3 px-5 py-4 border-b border-slate-200">
                    <BookOpen className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                        <h2 className="font-black text-slate-900">Notizen aus dem Handbuch einlesen</h2>
                        <p className="text-xs text-slate-500">
                            Handbuch vom {datumDeutsch(standIso) || 'unbekannten Datum'}. Eingelesen wird nur Text aus den
                            Notizfeldern. Handschriftliche Notizen auf Papier können nicht eingelesen werden.
                        </p>
                    </div>
                    <button type="button" onClick={onAbbrechen} className="p-1 text-slate-400 hover:text-slate-600" aria-label="Schließen">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto min-h-0 px-5 py-4 space-y-5">
                    {gesperrt && (
                        <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 text-xs text-amber-900 whitespace-pre-line">
                            {gesperrt}
                        </div>
                    )}
                    {erneut > 0 && (
                        <div className="bg-rose-50 border border-rose-200 rounded-xl px-3 py-2 text-xs text-rose-900 flex items-start gap-2">
                            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                            <span>
                                <strong>Es wurde nichts übernommen.</strong> {erneut} Notiz{erneut === 1 ? ' wurde' : 'en wurden'} seit
                                der Vorschau in der App geändert. Die Vorschau ist neu gerechnet; die betroffenen Einträge sind
                                markiert. Bitte prüfen und erneut bestätigen.
                            </span>
                        </div>
                    )}

                    {konflikte.length > 0 && (
                        <section>
                            <h3 className="flex items-center gap-1.5 text-xs font-black text-rose-700 uppercase tracking-wider mb-2">
                                <AlertTriangle className="w-3.5 h-3.5" /> Im Handbuch und in der App geändert ({konflikte.length})
                            </h3>
                            <div className="space-y-3">
                                {konflikte.map(v => (
                                    <div key={v.id} className={`border rounded-xl p-3 ${v.erneutBestaetigen ? 'border-rose-400 bg-rose-50/40' : 'border-rose-200'}`}>
                                        {v.erneutBestaetigen && (
                                            <div className="text-[11px] font-black text-rose-700 mb-1">{v.hinweis}</div>
                                        )}
                                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                                            <span className="text-sm font-bold text-slate-800 flex-1 min-w-0">{v.name}</span>
                                            <select
                                                value={auswahl[v.id] || v.vorschlag}
                                                onChange={e => setze(v.id, e.target.value as NotizAktion)}
                                                className="px-2 py-1 text-xs font-bold rounded-lg border border-slate-300"
                                                aria-label={`Aktion für ${v.name}`}
                                            >
                                                {(['anhaengen', 'ersetzen', 'ueberspringen'] as NotizAktion[]).map(a => (
                                                    <option key={a} value={a} disabled={a === 'anhaengen' && !v.neu}>{AKTION_LABEL[a]}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                                            <Textkasten titel="Beim Export" text={v.ausgang} />
                                            <Textkasten titel="Jetzt in der App" text={v.aktuell} />
                                            <Textkasten titel="Im Handbuch" text={v.neu} betont />
                                        </div>
                                        <p className="text-[11px] text-slate-500 mt-2">
                                            „Anhängen" behält den Text der App und setzt die Notiz aus dem Handbuch mit Datum darunter.
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    {aenderungen.length > 0 && (
                        <section>
                            <h3 className="text-xs font-black text-slate-500 uppercase tracking-wider mb-2">
                                Im Handbuch geändert ({aenderungen.length})
                            </h3>
                            <div className="space-y-3">
                                {aenderungen.map(v => (
                                    <div key={v.id} className="border border-slate-200 rounded-xl p-3">
                                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                                            <span className="text-sm font-bold text-slate-800 flex-1 min-w-0">
                                                {v.name}
                                                {v.neuerEintrag && (
                                                    <span className="ml-2 text-[10px] font-bold text-indigo-600">wird mit Status „offen" angelegt</span>
                                                )}
                                            </span>
                                            <select
                                                value={auswahl[v.id] || v.vorschlag}
                                                onChange={e => setze(v.id, e.target.value as NotizAktion)}
                                                className="px-2 py-1 text-xs font-bold rounded-lg border border-slate-300"
                                                aria-label={`Aktion für ${v.name}`}
                                            >
                                                <option value="ersetzen">Übernehmen</option>
                                                <option value="ueberspringen">Überspringen</option>
                                            </select>
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                            <Textkasten titel="Bisher" text={v.aktuell} />
                                            <Textkasten titel="Neu aus dem Handbuch" text={v.neu} betont />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    {uebersprungen.length > 0 && (
                        <section>
                            <h3 className="text-xs font-black text-slate-500 uppercase tracking-wider mb-2">
                                Übersprungen ({uebersprungen.length})
                            </h3>
                            <ul className="space-y-1">
                                {uebersprungen.map(v => (
                                    <li key={v.id} className="flex items-start gap-2 text-xs text-slate-600">
                                        <Info className="w-3.5 h-3.5 shrink-0 mt-0.5 text-slate-400" />
                                        <span><strong>{v.name}</strong>: {v.hinweis}</span>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}

                    <p className="text-xs text-slate-500">
                        {unveraendert} Notiz{unveraendert === 1 ? '' : 'en'} unverändert.
                        {unbekannteFelder > 0 && ` ${unbekannteFelder} fremde${unbekannteFelder === 1 ? 's Feld' : ' Felder'} im PDF ignoriert.`}
                        {konflikte.length + aenderungen.length === 0 && ' Es gibt nichts zu übernehmen.'}
                    </p>
                </div>

                <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200">
                    <button type="button" onClick={onAbbrechen} className="px-4 py-2 text-sm font-bold text-slate-600 hover:text-slate-800">
                        Abbrechen
                    </button>
                    <button
                        type="button"
                        onClick={() => onUebernehmen(auswahl)}
                        disabled={!!gesperrt || anzahl === 0}
                        className="px-4 py-2 text-sm font-bold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-40"
                    >
                        Übernehmen ({anzahl})
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    );
};
